/**
 * Het zoekgebied zonder React: buurten per wijk, zoeken op naam, een hele wijk
 * aan of uit, en of een concept afwijkt van wat er bewaard is.
 */
import type { BuurtenKaart } from '@mamimap/shared';

export type BuurtRegel = {
  id: number;
  buurtcode: string;
  buurtnaam: string;
  postcodes: string[];
};

export type Wijk = {
  wijkcode: string;
  wijknaam: string;
  buurten: BuurtRegel[];
};

const opNaam = new Intl.Collator('nl', { sensitivity: 'base', numeric: true });

/** De buurten gegroepeerd per wijk, allebei op naam. */
export function wijkenUit(kaart: BuurtenKaart | undefined): Wijk[] {
  if (!kaart) return [];
  const perWijk = new Map<string, Wijk>();
  for (const feature of kaart.features) {
    const p = feature.properties;
    let wijk = perWijk.get(p.wijkcode);
    if (!wijk) {
      wijk = { wijkcode: p.wijkcode, wijknaam: p.wijknaam, buurten: [] };
      perWijk.set(p.wijkcode, wijk);
    }
    wijk.buurten.push({
      id: feature.id,
      buurtcode: p.buurtcode,
      buurtnaam: p.buurtnaam,
      postcodes: p.postcodes,
    });
  }
  const wijken = [...perWijk.values()];
  for (const wijk of wijken) wijk.buurten.sort((a, b) => opNaam.compare(a.buurtnaam, b.buurtnaam));
  return wijken.sort((a, b) => opNaam.compare(a.wijknaam, b.wijknaam));
}

/**
 * Wijken met alleen de buurten die bij de zoekterm passen. Een term die op de
 * wijknaam past laat de hele wijk zien; een postcode vindt de buurten die hem
 * raken.
 */
export function zoekInWijken(wijken: readonly Wijk[], term: string): Wijk[] {
  const t = term.trim().toLocaleLowerCase('nl');
  if (t === '') return [...wijken];
  const uit: Wijk[] = [];
  for (const wijk of wijken) {
    if (wijk.wijknaam.toLocaleLowerCase('nl').includes(t)) {
      uit.push(wijk);
      continue;
    }
    const buurten = wijk.buurten.filter(
      (buurt) =>
        buurt.buurtnaam.toLocaleLowerCase('nl').includes(t) ||
        buurt.postcodes.some((pc) => pc.startsWith(t)),
    );
    if (buurten.length > 0) uit.push({ ...wijk, buurten });
  }
  return uit;
}

/** Hoeveel buurten van een wijk aan staan. */
export function telAan(wijk: Wijk, gekozen: ReadonlySet<string>): number {
  return wijk.buurten.filter((buurt) => gekozen.has(buurt.buurtcode)).length;
}

/** Een hele wijk: staat alles aan, dan gaat alles uit; anders gaat alles aan. */
export function wisselWijk(gekozen: ReadonlySet<string>, wijk: Wijk): Set<string> {
  const nieuw = new Set(gekozen);
  const allesAan = wijk.buurten.every((buurt) => gekozen.has(buurt.buurtcode));
  for (const buurt of wijk.buurten) {
    if (allesAan) nieuw.delete(buurt.buurtcode);
    else nieuw.add(buurt.buurtcode);
  }
  return nieuw;
}

export function wisselBuurt(gekozen: ReadonlySet<string>, buurtcode: string): Set<string> {
  const nieuw = new Set(gekozen);
  if (nieuw.has(buurtcode)) nieuw.delete(buurtcode);
  else nieuw.add(buurtcode);
  return nieuw;
}

export function zelfdeKeuze(a: ReadonlySet<string>, b: readonly string[]): boolean {
  return a.size === b.length && b.every((code) => a.has(code));
}

/* --- de zoekfilters (MA-27) ------------------------------------------------ */

/**
 * Wat er in het veld voor m² of budget staat, als getal. Leeg is geen filter
 * (null); `600.000` en `600 000` mogen, en een waarde die geen getal is of
 * niet positief is telt als leeg. Afgekapt op `maximum`, net als het contract.
 */
export function leesFilterGetal(tekst: string, maximum: number): number | null {
  const schoon = tekst.replace(/[\s.]/g, '').replace(',', '.');
  if (schoon === '') return null;
  const getal = Math.floor(Number(schoon));
  if (!Number.isFinite(getal) || getal <= 0) return null;
  return Math.min(getal, maximum);
}

/**
 * Of een nieuw budget lager is dan het opgeslagen: dan vallen er woningen uit
 * de zoekopdrachten en vraagt opslaan een bevestiging. Van geen budget naar
 * een budget is ook verlagen; andersom en gelijk niet.
 */
export function budgetVerlaagd(oud: number | null, nieuw: number | null): boolean {
  if (nieuw == null) return false;
  return oud == null || nieuw < oud;
}
